import React, {useContext, useEffect, useState} from 'react';
import axios from 'axios';
import config from '../../config.js';
import UserContext from '../common/UserContext.js';

// css
import '../../assets/css/Users.css';

// reactstrap
import {Col, Row} from 'reactstrap';

// components
import Alert from '../common/Alert.js';

// kendo react
import {Dialog, DialogActionsBar} from '@progress/kendo-react-dialogs';
import {Input, TextArea, RadioButton} from '@progress/kendo-react-inputs';
import {MultiSelect} from '@progress/kendo-react-dropdowns';
import {Hint, Label} from '@progress/kendo-react-labels';
import {Button} from '@progress/kendo-react-buttons';

// multilingual
import {useLocalization} from '@progress/kendo-react-intl';
import {
    aliasKey,
    cancelKey,
    completeRequiredFieldsKey,
    departmentKey,
    editUserKey,
    emailKey,
    enterValidEmailKey,
    hostsKey,
    invalidEmailKey,
    nameKey,
    notesKey,
    requiredFieldsMissing,
    saveHostIDKey,
    genericErrorTitleKey,
    errorOccurredRequestKey,
    hostUserAliasAlreadyExistsKey,
    hostUserHostIdRequiredKey,
    hostUserNotesRequiredKey,
    hostUserEmailRequiredKey,
    hostUserDeleteFailedKey,
    hostUserAliasRequiredKey,
    hostUserInvalidOperationKey,
    hostUserHostEmailExistsKey,
    statusKey,
    mainMessages
} from '../../assets/text/MultilingualText.js';
import {useSupportContext} from "../common/SupportContext";

function EditUserModal(props) {
    const hostUserErrorMessageMap = {
        MOD_HOSTUSER_1: hostUserAliasAlreadyExistsKey,
        MOD_HOSTUSER_2: hostUserHostIdRequiredKey,
        MOD_HOSTUSER_3: hostUserNotesRequiredKey,
        MOD_HOSTUSER_4: hostUserEmailRequiredKey,
        MOD_HOSTUSER_5: hostUserDeleteFailedKey,
        MOD_HOSTUSER_6: hostUserAliasRequiredKey,
        MOD_HOSTUSER_7: hostUserInvalidOperationKey,
        MOD_HOSTUSER_8: hostUserHostEmailExistsKey,
    };

    const {
        user,
        setUser,
        getUsers,
        setIsVisible,
        setIsLoading
    } = props;
    const {
        accessToken,
        siteLanguageDefault
    } = useContext(UserContext);
    const { dispatch } = useSupportContext();
    const localization = useLocalization();

    const titleStyle = {
        display: "flex",
        fontWeight: "normal",
        margin: 0,
        color: 'var(--color-text-primary)'
    };

    const [hostID, setHostID] = useState(
        user?.host_id
            ? user.host_id.split('|').map((host) => host.trim()).filter(Boolean)
            : []
    );
    const [alias, setAlias] = useState(user?.alias || '');
    const [name, setName] = useState(user?.name || '');
    const [notes, setNotes] = useState(user?.notes || '');
    const [email, setEmail] = useState(user?.email || '');
    const [department, setDepartment] = useState(user?.department || '');
    const [status, setStatus] = useState(user?.status === 'ACTIVE');


    const [isValid, setIsValid] = useState({
        alias: true,
        email: true,
        hostID: true,
        notes: true,
    });

    const [showErrors, setShowErrors] = useState({
        missingFields: false,
        invalidEmail: false,
        requestFailed: false
    });
    const [requestFailedMessageKey, setRequestFailedMessageKey] = useState(errorOccurredRequestKey);

    // clear error banners whenever any field changes
    useEffect(() => {
        setShowErrors({missingFields: false, invalidEmail: false, requestFailed: false});
        setRequestFailedMessageKey(errorOccurredRequestKey);
    }, [alias, name, email, hostID, notes, department]); // eslint-disable-line react-hooks/exhaustive-deps

    // closes the edit user modal
    const close = () => {
        setUser({});
        setIsVisible(isVisible => ({...isVisible, editUser: false}));
        // support metadata push
        dispatch({ type: 'UPDATE_TRANSACTION', payload: {phu_id: ""}});
    };

    const handleHostChange = (event) => {
        const values = [...event.target.value];
        const lastItem = values[values.length - 1];

        if (lastItem) {
            values.pop();
            const sameItem = values.find((value) => value === lastItem);
            if (sameItem === undefined) {
                values.push(lastItem);
            }
        }

        setHostID(values);
        setIsValid(v => ({...v, hostID: true}));
    };

    const validateEmail = (email) => {
        // Standard email validation regex - covers all common real-world addresses
        const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        return regex.test(email);
    };

    const fieldValues = {alias, email, hostID, notes};

    const validateUser = () => {
        const newIsValid = { alias: true, email: true, hostID: true, notes: true };

        // mark empty required fields invalid
        Object.keys(fieldValues).forEach(field => {
            if ((field === 'hostID' && fieldValues[field].length === 0) || (field !== 'hostID' && !fieldValues[field]?.trim())) {
                newIsValid[field] = false;
            }
        });

        const emailValid = validateEmail(email);
        if (!emailValid) newIsValid.email = false;

        setIsValid(newIsValid);

        const hasEmpty = Object.keys(newIsValid).some(
            field => !newIsValid[field] && field !== 'email'
        );
        const hasInvalidEmail = !emailValid;

        if (hasEmpty) setShowErrors(s => ({...s, missingFields: true}));
        if (hasInvalidEmail) setShowErrors(s => ({...s, invalidEmail: true}));

        if (!hasEmpty && !hasInvalidEmail) {
            editUser();
        }
    };

    /*
    * editUser() edits the user
    */
    const editUser = () => {
        let headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + accessToken
        };

        let data = {
            module: "HOST_USER",
            sub_module: "",
            action: "UPDATE",
            input_json: {
                phu_id: user.phu_id,
                host_id: hostID,
                alias: alias,
                name: name,
                notes: notes,
                email: email,
                department: department,
                status: status ? "ACTIVE" : "INACTIVE",
                req_source: "host_user"
            }
        };

        setIsLoading(true);

        axios.post(
            config.users.USERS,
            data,
            {headers: headers}
        )
            .then((response) => {
                if (response.status === 200) {
                    setIsLoading(false);
                    getUsers();
                    close();
                }
            })
            .catch((error) => {
                console.log("ERROR: Failed to UPDATE user", error);
                let errorMessageKey = errorOccurredRequestKey;
                if (error.response?.status === 400 && error.response?.hasOwnProperty('data')) {
                    const errorCode = error.response?.data?.error_code?.toUpperCase();
                    errorMessageKey = hostUserErrorMessageMap[errorCode] || errorOccurredRequestKey;
                }

                setRequestFailedMessageKey(errorMessageKey);
                setShowErrors((s) => ({...s, requestFailed: true}));
                setIsLoading(false);
            });
    };

    return (
        <Dialog
            className={'user-modal ksm-dialog'}
            title={<div style={titleStyle}>
                {localization.toLanguageString(editUserKey, mainMessages[siteLanguageDefault][editUserKey])}
            </div>}
            onClose={close}
        >
            <Row className={'align-items-stretch'}>
                <Col xs={4} className={'d-flex flex-column'}>
                    <Label>
                        {localization.toLanguageString(aliasKey, mainMessages[siteLanguageDefault][aliasKey])}
                        <span style={{color: "var(--color-alert-danger-base)"}}>*</span>
                    </Label>
                    <Input
                        value={alias}
                        maxLength={350}
                        valid={isValid.alias}
                        onChange={(e) => { setAlias(e.value); setIsValid(v => ({...v, alias: true})); }}
                        autoFocus={true}
                    />
                    <Label className={'mt-2'}>
                        {localization.toLanguageString(emailKey, mainMessages[siteLanguageDefault][emailKey])}
                        <span style={{color: "var(--color-alert-danger-base)"}}>*</span>
                    </Label>
                    <Input
                        value={email}
                        maxLength={150}
                        valid={isValid.email}
                        onChange={(e) => { setEmail(e.value); setIsValid(v => ({...v, email: true})); }}
                    />
                </Col>
                <Col xs={4} className={'d-flex flex-column'}>
                    <Label>
                        {localization.toLanguageString(nameKey, mainMessages[siteLanguageDefault][nameKey])}
                    </Label>
                    <Input
                        value={name}
                        maxLength={150}
                        onChange={(e) => { setName(e.value); }}
                    />
                    <Label className={'mt-2'}>
                        {localization.toLanguageString(departmentKey, mainMessages[siteLanguageDefault][departmentKey])}
                    </Label>
                    <Input
                        value={department}
                        maxLength={150}
                        onChange={(e) => { setDepartment(e.value); }}
                    />
                </Col>
                <Col xs={4} className={'d-flex flex-column'}>
                    <Label>
                        {localization.toLanguageString(notesKey, mainMessages[siteLanguageDefault][notesKey])}
                        <span style={{color: "var(--color-alert-danger-base)"}}>*</span>
                    </Label>
                    <TextArea
                        className={'flex-grow-1'}
                        value={notes}
                        maxLength={500}
                        valid={isValid.notes}
                        resizable={"none"}
                        onChange={(e) => { setNotes(e.value); setIsValid(v => ({...v, notes: true})); }}
                    />
                </Col>
            </Row>
            <Row className={'mt-2'}>
                <Col xs={8}>
                    <Label>
                        {localization.toLanguageString(hostsKey, mainMessages[siteLanguageDefault][hostsKey])}
                        <span style={{color: "var(--color-alert-danger-base)"}}>*</span>
                    </Label>
                    <MultiSelect
                        value={hostID}
                        data={hostID}
                        valid={isValid.hostID}
                        onChange={handleHostChange}
                        allowCustom={true}
                        style={{width: '100%'}}
                        placeholder={'Enter hosts'}
                    />
                    <Hint>
                        Input a Host ID and click "Enter" to add the host
                    </Hint>
                </Col>
            </Row>
            <Row className={'mt-2'}>
                <Col xs={4}>
                    <Label className={'mt-2'}>
                        {localization.toLanguageString(statusKey, mainMessages[siteLanguageDefault][statusKey])}
                    </Label>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <div className={"me-1"}>
                            <RadioButton
                                name={'status'}
                                value={'ACTIVE'}
                                checked={status}
                                label={'Active'}
                                onChange={() => setStatus(true)}
                            />
                        </div>
                        <div className={"ms-1"}>
                            <RadioButton
                                name={'status'}
                                value={'INACTIVE'}
                                checked={!status}
                                label={'Inactive'}
                                onChange={() => setStatus(false)}
                            />
                        </div>
                    </div>
                </Col>
            </Row>
            {showErrors.missingFields && (
                <Row className={'mt-2'}>
                    <Col>
                        <Alert
                            type={'error'}
                            title={localization.toLanguageString(requiredFieldsMissing, mainMessages[siteLanguageDefault][requiredFieldsMissing])}
                            message={localization.toLanguageString(completeRequiredFieldsKey, mainMessages[siteLanguageDefault][completeRequiredFieldsKey])}
                            showHandler={() => setShowErrors(s => ({...s, missingFields: false}))}
                        />
                    </Col>
                </Row>
            )}
            {showErrors.invalidEmail && (
                <Row className={'mt-2'}>
                    <Col>
                        <Alert
                            type={'error'}
                            title={localization.toLanguageString(invalidEmailKey, mainMessages[siteLanguageDefault][invalidEmailKey])}
                            message={localization.toLanguageString(enterValidEmailKey, mainMessages[siteLanguageDefault][enterValidEmailKey])}
                            showHandler={() => setShowErrors(s => ({...s, invalidEmail: false}))}
                        />
                    </Col>
                </Row>
            )}
            {showErrors.requestFailed && (
                <Row className={'mt-2'}>
                    <Col>
                        <Alert
                            type={'error'}
                            title={localization.toLanguageString(genericErrorTitleKey, mainMessages[siteLanguageDefault][genericErrorTitleKey])}
                            message={localization.toLanguageString(requestFailedMessageKey, mainMessages[siteLanguageDefault][requestFailedMessageKey])}
                            showHandler={() => setShowErrors(s => ({...s, requestFailed: false}))}
                        />
                    </Col>
                </Row>
            )}
            <DialogActionsBar layout='center'>
                <Button
                    themeColor={"primary"}
                    size={"large"}
                    fillMode={"outline"}
                    type={"button"}
                    onClick={close}
                >
                    {localization.toLanguageString(cancelKey, mainMessages[siteLanguageDefault][cancelKey])}
                </Button>
                <Button
                    themeColor={"primary"}
                    size={"large"}
                    fillMode={"solid"}
                    type={"button"}
                    onClick={validateUser}
                >
                    {localization.toLanguageString(saveHostIDKey, mainMessages[siteLanguageDefault][saveHostIDKey])}
                </Button>
            </DialogActionsBar>
        </Dialog>
    )
}

export default EditUserModal;